import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { del, get, post, put } from "../services/apiClient";

export interface PlanFeatureRow {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  category: string;
  isEnabled: boolean;
  limits?: Record<string, unknown> | null;
}

export interface SubscriptionPlanRow {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  priceMonthly: number | string;
  priceYearly?: number | string | null;
  yearlyDiscountPct?: number | string | null;
  currency: string;
  maxBusinesses?: number | null;
  maxRooms?: number | null;
  trialDays?: number | null;
  isDefault: boolean;
  isActive: boolean;
  sortOrder?: number | null;
  gatewayPlanIds?: Record<string, string> | null;
  features: PlanFeatureRow[];
  createdAt: string;
  updatedAt?: string;
}

interface PlanListResponse {
  data: SubscriptionPlanRow[];
}

export interface CreatePlanInput {
  code: string;
  name: string;
  description?: string;
  priceMonthly: number;
  priceYearly?: number;
  yearlyDiscountPct?: number;
  currency?: string;
  maxBusinesses?: number;
  maxRooms?: number;
  trialDays?: number;
  isDefault?: boolean;
  isActive?: boolean;
  sortOrder?: number;
}

export type UpdatePlanInput = Partial<CreatePlanInput>;

export interface SetPlanFeaturesInput {
  featureIds: string[];
  limits?: Array<{ featureId: string; limits?: Record<string, unknown> }>;
}

const LIST_KEY = ["subscription-plans"] as const;

export const useSubscriptionPlans = () =>
  useQuery<PlanListResponse>({
    queryKey: LIST_KEY,
    queryFn: () => get<PlanListResponse>("/admin/plans"),
    retry: false,
  });

export const useCreatePlan = () => {
  const qc = useQueryClient();
  return useMutation<SubscriptionPlanRow, Error, CreatePlanInput>({
    mutationFn: (data) => post<SubscriptionPlanRow>("/admin/plans", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
};

export const useUpdateSubscriptionPlan = () => {
  const qc = useQueryClient();
  return useMutation<
    SubscriptionPlanRow,
    Error,
    { id: string; data: UpdatePlanInput }
  >({
    mutationFn: ({ id, data }) =>
      put<SubscriptionPlanRow>(`/admin/plans/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
};

export const useDeletePlan = () => {
  const qc = useQueryClient();
  return useMutation<{ success: true }, Error, string>({
    mutationFn: (id) => del<{ success: true }>(`/admin/plans/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
};

export const useSetPlanFeatures = () => {
  const qc = useQueryClient();
  return useMutation<
    SubscriptionPlanRow,
    Error,
    { id: string; data: SetPlanFeaturesInput }
  >({
    mutationFn: ({ id, data }) =>
      put<SubscriptionPlanRow>(`/admin/plans/${id}/features`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
};
